import { z } from "zod";
export { ToolBase, hasStreamingSupport } from "./toolBase";
class ToolDescription {
    /**
     * Creates a ToolDescription for LLM function calling.
     *
     * @param name - Tool name
     * @param description - Tool description
     * @param parametersOrSchema - Either a Zod schema (recommended) or a manual JSON schema object (backward compatible)
     * @param systemPromptUsage - Optional: How this tool should appear in system prompts
     *
     * @example
     * // Using Zod schema (recommended):
     * const schema = z.object({ name: z.string(), count: z.number() });
     * new ToolDescription("myTool", "Does something", schema);
     *
     * @example
     * // Using manual JSON schema (backward compatible):
     * new ToolDescription("myTool", "Does something", {
     *   type: 'object',
     *   properties: { name: { type: 'string' } },
     *   required: ['name']
     * });
     *
     * @example
     * // With system prompt usage:
     * new ToolDescription("myTool", "Does something", schema, {
     *   briefDescription: "Performs a task",
     *   whenToUse: ["When user asks to do X", "When data needs processing"],
     *   exampleUsage: '{ "param": "value" }',
     * });
     */
    constructor(name, description, parametersOrSchema, systemPromptUsage) {
        this.type = 'function';
        // Check if it's a Zod schema by looking for the _def property
        const isZodSchema = parametersOrSchema &&
            typeof parametersOrSchema === 'object' &&
            '_def' in parametersOrSchema &&
            typeof parametersOrSchema.parse === 'function';
        if (isZodSchema) {
            // It's a Zod schema - convert to JSON schema for LLM function calling
            // Use Zod 4's native JSON Schema conversion
            const jsonSchema = z.toJSONSchema(parametersOrSchema);
            // Ensure the schema has type: "object" at the root (required by Anthropic)
            const schemaWithType = jsonSchema;
            if (!schemaWithType.type) {
                schemaWithType.type = "object";
            }
            this.function = {
                name: name,
                description: description,
                parameters: jsonSchema,
            };
        }
        else {
            // It's a manual JSON schema - use as-is (backward compatible)
            this.function = {
                name: name,
                description: description,
                parameters: parametersOrSchema,
            };
        }
        // Store system prompt usage for auto-generating "Available Tools" sections
        this.systemPromptUsage = systemPromptUsage;
    }
    toJson() {
        return JSON.stringify(this);
    }
}
/**
 * Pre-process parameters to handle LLM quirks where nested objects
 * are passed as JSON strings instead of actual objects.
 *
 * @param params - Raw parameters from LLM
 * @returns Processed parameters with stringified JSON parsed back to objects
 */
function preprocessParams(params) {
    if (params === null || params === undefined) {
        return params;
    }
    if (typeof params === 'string') {
        // Try to parse as JSON if it looks like an object or array
        const trimmed = params.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                return JSON.parse(trimmed);
            }
            catch {
                return params; // Return original string if parsing fails
            }
        }
        return params;
    }
    if (Array.isArray(params)) {
        return params.map(preprocessParams);
    }
    if (typeof params === 'object') {
        const processed = {};
        for (const [key, value] of Object.entries(params)) {
            processed[key] = preprocessParams(value);
        }
        return processed;
    }
    return params;
}
export function safeParseToolParams(schema, params) {
    // Pre-process to handle stringified JSON from LLM
    const processedParams = preprocessParams(params);
    const result = schema.safeParse(processedParams);
    if (result.success) {
        return { success: true, data: result.data };
    }
    // Format Zod errors into a readable message
    const errorMessages = result.error.issues.map(err => {
        const path = err.path.length > 0 ? err.path.join('.') : 'root';
        return `${path}: ${err.message}`;
    });
    return {
        success: false,
        error: `Validation failed:\n${errorMessages.join('\n')}`
    };
}
export { ToolDescription };
